import { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, Button, Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';

import { createVoiceAssistantConfig, updateVoiceAssistantConfig, getVoiceAssistantConfigById } from './FormikHelpers/Api';
import {
  emptyVoiceConfig, setNestedValue, deepMerge,
  CLAUDE_MODELS, STT_PROVIDERS, TTS_PROVIDERS, POLLY_VOICES, POLLY_ENGINES,
  AWS_REGIONS, PERSONA_TONES, LANGUAGES, LEAD_FIELDS, WEEK_DAYS,
} from './FormikHelpers/InitialValues';
import { FieldGroup, SectionHeader, SectionDivider, Toggle, ToggleRow, InfoBox, Row2, TagListInput, inp, lbl } from './VoiceAssistantShared';

const PersonaSection = ({ values, set }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <SectionHeader icon="🎭" title="Persona" sub="Name, tone, and greeting messages" />

    <Row2>
      <FieldGroup label="Assistant Name">
        <input
          style={inp}
          value={values.persona.assistant_name}
          onChange={(e) => set('persona.assistant_name', e.target.value)}
          placeholder="Aria"
        />
      </FieldGroup>
      <FieldGroup label="Tone">
        <select style={inp} value={values.persona.tone} onChange={(e) => set('persona.tone', e.target.value)}>
          {PERSONA_TONES.map((t) => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>
      </FieldGroup>
    </Row2>

    <Row2>
      <FieldGroup label="Language">
        <select style={inp} value={values.persona.language} onChange={(e) => set('persona.language', e.target.value)}>
          {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </FieldGroup>
      <FieldGroup
        label={`Speaking Rate — ${values.persona.speaking_rate}×`}
        hint="0.5 = slow  ·  1.0 = normal  ·  2.0 = fast"
      >
        <input
          type="range" min="0.5" max="2.0" step="0.1"
          value={values.persona.speaking_rate}
          onChange={(e) => set('persona.speaking_rate', parseFloat(e.target.value))}
          style={{ width: '100%', accentColor: '#1D4ED8' }}
        />
      </FieldGroup>
    </Row2>

    <ToggleRow
      label="Enable Filler Words"
      sub='Say "Sure!", "Got it!" etc. to sound more human'
      checked={values.persona.filler_words_enabled}
      onChange={(v) => set('persona.filler_words_enabled', v)}
    />

    <FieldGroup label="Intro Message" hint="Spoken immediately when a call connects">
      <textarea
        style={{ ...inp, resize: 'vertical', minHeight: 72 }}
        value={values.persona.intro_message}
        onChange={(e) => set('persona.intro_message', e.target.value)}
      />
    </FieldGroup>

    <FieldGroup label="Goodbye Message" hint="Spoken before the call is ended by the AI">
      <textarea
        style={{ ...inp, resize: 'vertical', minHeight: 56 }}
        value={values.persona.goodbye_message}
        onChange={(e) => set('persona.goodbye_message', e.target.value)}
      />
    </FieldGroup>
  </div>
);

const AISection = ({ values, set }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <SectionHeader icon="🧠" title="AI Configuration" sub="Bedrock model, prompts, knowledge base, and conversation limits" />

    <Row2>
      <FieldGroup label="Claude Model (via Bedrock)">
        <select style={inp} value={values.ai.model} onChange={(e) => set('ai.model', e.target.value)}>
          {CLAUDE_MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </FieldGroup>
      <FieldGroup
        label={`Max Tokens / Turn — ${values.ai.max_tokens_per_turn}`}
        hint="Keep 150–250 for fast voice responses"
      >
        <input
          type="range" min="50" max="500" step="10"
          value={values.ai.max_tokens_per_turn}
          onChange={(e) => set('ai.max_tokens_per_turn', parseInt(e.target.value))}
          style={{ width: '100%', accentColor: '#1D4ED8' }}
        />
      </FieldGroup>
    </Row2>

    <Row2>
      <FieldGroup
        label={`Temperature — ${values.ai.temperature}`}
        hint="Higher = more creative  ·  Lower = more precise"
      >
        <input
          type="range" min="0" max="1" step="0.05"
          value={values.ai.temperature}
          onChange={(e) => set('ai.temperature', parseFloat(e.target.value))}
          style={{ width: '100%', accentColor: '#1D4ED8' }}
        />
      </FieldGroup>
      <FieldGroup
        label={`Max Conversation Turns — ${values.ai.max_conversation_turns}`}
        hint="Call ends with goodbye after this many turns"
      >
        <input
          type="range" min="5" max="50" step="1"
          value={values.ai.max_conversation_turns}
          onChange={(e) => set('ai.max_conversation_turns', parseInt(e.target.value))}
          style={{ width: '100%', accentColor: '#1D4ED8' }}
        />
      </FieldGroup>
    </Row2>

    <FieldGroup
      label="System Prompt Override"
      optional
      hint="Leave blank to auto-generate from persona settings. Set this to take full control."
    >
      <textarea
        style={{ ...inp, resize: 'vertical', minHeight: 90, fontFamily: 'monospace', fontSize: 11.5 }}
        value={values.ai.system_prompt}
        onChange={(e) => set('ai.system_prompt', e.target.value)}
        placeholder="Leave blank to use the auto-generated prompt…"
      />
    </FieldGroup>

    <FieldGroup
      label="Knowledge Base"
      hint="Injected into every conversation — be specific about units, pricing, policies, FAQs"
    >
      <textarea
        style={{ ...inp, resize: 'vertical', minHeight: 120 }}
        value={values.ai.knowledge_base}
        onChange={(e) => set('ai.knowledge_base', e.target.value)}
        placeholder={`Enter property details, pricing, policies, FAQs…\n\nExample:\n- 2-bed units: $1,800/mo, available May 1\n- Studios: $1,100/mo, available now\n- Pet policy: cats OK, no dogs\n- Parking: $75/mo`}
      />
    </FieldGroup>

    <FieldGroup
      label="Lead Collection Prompt"
      hint="Tells the AI when and how to ask for contact information"
    >
      <textarea
        style={{ ...inp, resize: 'vertical', minHeight: 64 }}
        value={values.ai.lead_collection_prompt}
        onChange={(e) => set('ai.lead_collection_prompt', e.target.value)}
      />
    </FieldGroup>

    <FieldGroup
      label="Fallback Message"
      hint="Rarely used — AI decides the next step, this is a hard-fail safety net"
    >
      <input
        style={inp}
        value={values.ai.fallback_message}
        onChange={(e) => set('ai.fallback_message', e.target.value)}
      />
    </FieldGroup>

    <InfoBox>
      💡 <strong>Powered by AWS Bedrock.</strong> The model runs securely in your AWS environment — no data leaves your cloud. A rich knowledge base is the single biggest lever for call quality.
    </InfoBox>
  </div>
);

const STTSection = ({ values, set }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <SectionHeader icon="👂" title="Speech-to-Text" sub="AWS Transcribe transcribes the caller's voice to text in real-time" />

    <Row2>
      <FieldGroup label="Provider">
        <select style={inp} value={values.stt.provider} onChange={(e) => set('stt.provider', e.target.value)}>
          {STT_PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </FieldGroup>
      <FieldGroup label="Language">
        <select style={inp} value={values.stt.language} onChange={(e) => set('stt.language', e.target.value)}>
          {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </FieldGroup>
    </Row2>

    <FieldGroup
      label={`End-of-Speech Timeout — ${values.stt.endpointing_ms} ms`}
      hint="How long to wait for silence before sending to Transcribe"
    >
      <input
        type="range" min="200" max="2000" step="100"
        value={values.stt.endpointing_ms}
        onChange={(e) => set('stt.endpointing_ms', parseInt(e.target.value))}
        style={{ width: '100%', accentColor: '#1D4ED8' }}
      />
    </FieldGroup>

    <ToggleRow
      label="Add Punctuation"
      sub="Improves readability of transcripts and AI comprehension"
      checked={values.stt.punctuate}
      onChange={(v) => set('stt.punctuate', v)}
    />
    <ToggleRow
      label="Interim Results"
      sub="Process speech as it happens — reduces response latency"
      checked={values.stt.interim_results}
      onChange={(v) => set('stt.interim_results', v)}
    />

    <InfoBox>
      🎙 <strong>AWS Transcribe</strong> supports real-time streaming transcription. Enabling interim results reduces perceived latency by processing partial utterances before the caller finishes speaking.
    </InfoBox>
  </div>
);

const TTSSection = ({ values, set }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <SectionHeader icon="🔊" title="Text-to-Speech" sub="AWS Polly converts AI text responses to spoken voice" />

    <Row2>
      <FieldGroup label="Voice">
        <select style={inp} value={values.tts.voice_id} onChange={(e) => set('tts.voice_id', e.target.value)}>
          {POLLY_VOICES.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>
      </FieldGroup>
      <FieldGroup label="Engine" hint="Neural and Generative sound the most natural">
        <select style={inp} value={values.tts.engine} onChange={(e) => set('tts.engine', e.target.value)}>
          {POLLY_ENGINES.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
        </select>
      </FieldGroup>
    </Row2>

    <InfoBox>
      🔊 <strong>AWS Polly</strong> is fully managed and scales automatically with your call volume. Use <strong>Neural</strong> for the best balance of quality and cost, or <strong>Generative</strong> for the most lifelike voice. Not all voices support all engines — if you get an error, switch to Neural or Standard.
    </InfoBox>
  </div>
);

const IntegrationsSection = ({ values, set, propertyList }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <SectionHeader icon="🔌" title="Integrations" sub="AWS Connect, notifications, CRM, and booking" />

    <SectionDivider label="AWS Connect" />

    <FieldGroup label="AWS Region">
      <select style={inp} value={values.integrations.aws_region} onChange={(e) => set('integrations.aws_region', e.target.value)}>
        {AWS_REGIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
    </FieldGroup>

    <FieldGroup label="Connect Phone Number" hint="The AWS Connect number callers will dial — must be claimed in your Connect instance">
      <input
        style={inp}
        value={values.integrations.connect_phone_number}
        onChange={(e) => set('integrations.connect_phone_number', e.target.value)}
        placeholder="+16135550100"
      />
    </FieldGroup>

    <Row2>
      <FieldGroup label="Connect Instance ID" hint="Found in AWS Connect → Overview">
        <input
          style={inp}
          value={values.integrations.connect_instance_id}
          onChange={(e) => set('integrations.connect_instance_id', e.target.value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
        />
      </FieldGroup>
      <FieldGroup label="Contact Flow ID" hint="The flow that routes calls to this assistant">
        <input
          style={inp}
          value={values.integrations.connect_contact_flow_id}
          onChange={(e) => set('integrations.connect_contact_flow_id', e.target.value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
        />
      </FieldGroup>
    </Row2>

    <InfoBox color="#D97706">
      ☁️ <strong>AWS Connect setup:</strong> Claim a phone number in your Connect instance, create a Contact Flow that invokes this assistant's Lambda, and paste the Instance ID and Contact Flow ID above. IAM permissions for Transcribe, Polly, and Bedrock must be attached to the Lambda execution role.
    </InfoBox>

    <SectionDivider label="Notifications" />

    <Row2>
      <FieldGroup label="Notification Email" hint="Lead emails are sent here">
        <input
          style={inp}
          type="email"
          value={values.integrations.notification_email}
          onChange={(e) => set('integrations.notification_email', e.target.value)}
          placeholder="manager@yourco.ca"
        />
      </FieldGroup>
      <FieldGroup label="Recipient Name">
        <input
          style={inp}
          value={values.integrations.notification_email_name}
          onChange={(e) => set('integrations.notification_email_name', e.target.value)}
          placeholder="Team"
        />
      </FieldGroup>
    </Row2>

    <SectionDivider label="Property & Booking" />

    <Row2>
      <FieldGroup label="Linked Property" optional>
        <select
          style={inp}
          value={values.integrations.property_id || ''}
          onChange={(e) => set('integrations.property_id', e.target.value || '')}
        >
          <option value="">None</option>
          {(propertyList || []).map((p) => (
            <option key={p._id} value={p._id}>{p.title}{p.location ? `, ${p.location}` : ''}</option>
          ))}
        </select>
      </FieldGroup>
      <FieldGroup label="Booking URL" optional hint="Shared verbally when callers want to book a showing">
        <input
          style={inp}
          value={values.integrations.booking_url}
          onChange={(e) => set('integrations.booking_url', e.target.value)}
          placeholder="https://cal.com/yourproperty"
        />
      </FieldGroup>
    </Row2>

    <SectionDivider label="CRM" />

    <FieldGroup label="CRM Webhook URL" optional hint="Lead data is POSTed here when a lead is captured">
      <input
        style={inp}
        value={values.integrations.crm_webhook}
        onChange={(e) => set('integrations.crm_webhook', e.target.value)}
        placeholder="https://hooks.zapier.com/…"
      />
    </FieldGroup>
  </div>
);

const CallLimitsSection = ({ values, set }) => {
  const toggleDay = (num) => {
    const cur = values.call_limits.business_days || [];
    set(
      'call_limits.business_days',
      cur.includes(num) ? cur.filter((d) => d !== num) : [...cur, num].sort((a, b) => a - b)
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SectionHeader icon="⏱" title="Call Limits" sub="Duration, concurrency, business hours, and blocked numbers" />

      <Row2>
        <FieldGroup
          label={`Max Call Duration — ${Math.floor(values.call_limits.max_call_duration_seconds / 60)} min`}
          hint="Call is force-ended after this duration"
        >
          <input
            type="range" min="60" max="1800" step="60"
            value={values.call_limits.max_call_duration_seconds}
            onChange={(e) => set('call_limits.max_call_duration_seconds', parseInt(e.target.value))}
            style={{ width: '100%', accentColor: '#1D4ED8' }}
          />
        </FieldGroup>
        <FieldGroup
          label={`Max Concurrent Calls — ${values.call_limits.max_concurrent_calls}`}
          hint="Additional calls receive the outside-hours message"
        >
          <input
            type="range" min="1" max="20" step="1"
            value={values.call_limits.max_concurrent_calls}
            onChange={(e) => set('call_limits.max_concurrent_calls', parseInt(e.target.value))}
            style={{ width: '100%', accentColor: '#1D4ED8' }}
          />
        </FieldGroup>
      </Row2>

      <ToggleRow
        label="Business Hours Only"
        sub="Calls outside the set hours play the outside-hours message and hang up"
        checked={values.call_limits.business_hours_only}
        onChange={(v) => set('call_limits.business_hours_only', v)}
      />

      {values.call_limits.business_hours_only && (
        <>
          <Row2>
            <FieldGroup label="Open Time">
              <input
                style={inp}
                type="time"
                value={values.call_limits.business_hours_start}
                onChange={(e) => set('call_limits.business_hours_start', e.target.value)}
              />
            </FieldGroup>
            <FieldGroup label="Close Time">
              <input
                style={inp}
                type="time"
                value={values.call_limits.business_hours_end}
                onChange={(e) => set('call_limits.business_hours_end', e.target.value)}
              />
            </FieldGroup>
          </Row2>

          <FieldGroup label="Open Days">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {WEEK_DAYS.map(({ num, label }) => {
                const active = (values.call_limits.business_days || []).includes(num);
                return (
                  <button
                    key={num}
                    type="button"
                    onClick={() => toggleDay(num)}
                    style={{
                      width: 40, height: 40, borderRadius: 9,
                      border: `1.5px solid ${active ? '#1D4ED8' : '#E2E8F0'}`,
                      background: active ? '#EFF6FF' : '#fff',
                      color: active ? '#1D4ED8' : '#94A3B8',
                      fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    }}
                  >{label}</button>
                );
              })}
            </div>
          </FieldGroup>

          <FieldGroup label="Outside Hours Message">
            <textarea
              style={{ ...inp, resize: 'vertical', minHeight: 64 }}
              value={values.call_limits.outside_hours_message}
              onChange={(e) => set('call_limits.outside_hours_message', e.target.value)}
            />
          </FieldGroup>
        </>
      )}

      <FieldGroup
        label="Blocked Numbers"
        optional
        hint="One number per line — calls from these are silently rejected"
      >
        <textarea
          style={{ ...inp, resize: 'vertical', minHeight: 72, fontFamily: 'monospace', fontSize: 11.5 }}
          value={(values.call_limits.blocked_numbers || []).join('\n')}
          onChange={(e) =>
            set('call_limits.blocked_numbers', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))
          }
          placeholder={`+16135550000\n+14165550000`}
        />
      </FieldGroup>
    </div>
  );
};

const LeadConfigSection = ({ values, set }) => {
  const toggleField = (f) => {
    const cur = values.lead_config.required_fields || [];
    set(
      'lead_config.required_fields',
      cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SectionHeader icon="📋" title="Lead Collection" sub="What to capture, required fields, and SMS follow-up" />

      <ToggleRow
        label="Auto-Collect Leads"
        sub="AI naturally asks for contact info when the caller shows interest"
        checked={values.lead_config.auto_collect}
        onChange={(v) => set('lead_config.auto_collect', v)}
      />

      <ToggleRow
        label="Ask for Callback Time"
        sub={`"When's a good time for us to call you back?"`}
        checked={values.lead_config.ask_for_callback_time}
        onChange={(v) => set('lead_config.ask_for_callback_time', v)}
      />

      <FieldGroup label="Required Fields" hint="Lead is only saved when all required fields are collected">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {LEAD_FIELDS.map((f) => {
            const active = (values.lead_config.required_fields || []).includes(f);
            return (
              <button
                key={f}
                type="button"
                onClick={() => toggleField(f)}
                style={{
                  padding: '5px 14px', borderRadius: 20,
                  border: `1.5px solid ${active ? '#1D4ED8' : '#E2E8F0'}`,
                  background: active ? '#EFF6FF' : '#fff',
                  color: active ? '#1D4ED8' : '#94A3B8',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  transition: 'all .12s',
                }}
              >{f}</button>
            );
          })}
        </div>
      </FieldGroup>

      <ToggleRow
        label="Send Confirmation SMS"
        sub="Texts the caller after the lead is captured (requires AWS SNS or Connect)"
        checked={values.lead_config.send_confirmation_sms}
        onChange={(v) => set('lead_config.send_confirmation_sms', v)}
      />

      {values.lead_config.send_confirmation_sms && (
        <FieldGroup label="SMS Message">
          <textarea
            style={{ ...inp, resize: 'vertical', minHeight: 56 }}
            value={values.lead_config.confirmation_sms_message}
            onChange={(e) => set('lead_config.confirmation_sms_message', e.target.value)}
          />
        </FieldGroup>
      )}

      <InfoBox>
        🔄 <strong>How lead capture works:</strong> The AI collects fields naturally during conversation. When all required fields are collected the lead is saved, a notification email is sent via AWS SES, and the CRM webhook fires automatically — no manual action needed.
      </InfoBox>
    </div>
  );
};

const TABS = [
  { key: 'persona', label: '🎭 Persona' },
  { key: 'ai', label: '🧠 AI' },
  { key: 'stt', label: '👂 Speech-to-Text' },
  { key: 'tts', label: '🔊 Text-to-Speech' },
  { key: 'integrations', label: '🔌 Integrations' },
  { key: 'call_limits', label: '⏱ Call Limits' },
  { key: 'lead_config', label: '📋 Leads' },
];

const VoiceAssistantConfigModal = ({ mode, open, onClose, onSaved, existingConfig, propertyList }) => {
  const [values, setValues] = useState(emptyVoiceConfig());
  const [saving, setSaving] = useState(false);
  const [loadingConfig, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('persona');
  const [slugError, setSlugError] = useState('');

  const isEditMode = mode === 'edit';

  const fetchFreshConfig = useCallback(async (configId) => {
    if (!configId) return;
    setLoading(true);
    try {
      const res = await getVoiceAssistantConfigById(configId);
      if (res?.data) {
        setValues(deepMerge(emptyVoiceConfig(), res.data));
      }
    } catch {
      toast.error('Failed to load voice assistant configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (isEditMode && existingConfig?._id) {
      fetchFreshConfig(existingConfig._id);
    } else {
      setValues(emptyVoiceConfig());
    }
    setActiveTab('persona');
    setSlugError('');
  }, [open, mode, existingConfig, fetchFreshConfig]);

  const set = useCallback((path, value) => {
    setValues((prev) => setNestedValue(prev, path, value));
  }, []);

  const handleSubmit = async () => {
    if (!values.voice_slug?.trim()) {
      setSlugError('Voice slug is required');
      setActiveTab('persona');
      return;
    }
    if (!/^[a-z0-9-]+$/.test(values.voice_slug)) {
      setSlugError('Only lowercase letters, numbers, and hyphens');
      setActiveTab('persona');
      return;
    }
    setSlugError('');
    setSaving(true);

    try {
      const payload = {
        voice_slug: values.voice_slug.trim().toLowerCase(),
        is_active: values.is_active,
        persona: values.persona,
        stt: values.stt,
        tts: values.tts,
        ai: values.ai,
        integrations: values.integrations,
        call_limits: values.call_limits,
        lead_config: values.lead_config,
      };

      if (isEditMode && existingConfig?._id) {
        await updateVoiceAssistantConfig(existingConfig._id, payload);
        toast.success('Voice assistant updated');
      } else {
        await createVoiceAssistantConfig(payload);
        toast.success('Voice assistant created');
      }

      if (onSaved) onSaved();
      onClose();
    } catch { }
    finally { setSaving(false); }
  };

  const tabStyle = (key) => ({
    padding: '9px 16px',
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    borderBottom: activeTab === key ? '2px solid #1D4ED8' : '2px solid transparent',
    background: 'transparent',
    color: activeTab === key ? '#1D4ED8' : '#64748B',
    transition: 'all 0.12s ease',
    whiteSpace: 'nowrap',
  });

  return (
    <Modal isOpen={open} toggle={onClose} size="xl" centered style={{ maxWidth: '96vw' }}>
      <Form>
        <ModalHeader toggle={onClose} style={{ background: '#0F1C2E', borderBottom: 'none', padding: '14px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(79,142,247,0.15)', border: '1px solid rgba(79,142,247,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
              🎙
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#F0F9FF' }}>
                {isEditMode ? `✏️ Edit: ${existingConfig?.persona?.assistant_name || existingConfig?.voice_slug || 'Assistant'}` : '✨ New Voice Assistant'}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(147,197,253,0.6)', marginTop: 2 }}>
                Powered by AWS Connect · Bedrock · Transcribe · Polly
              </div>
            </div>

            <div style={{ marginLeft: 'auto', marginRight: 32, display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ ...lbl, color: 'rgba(147,197,253,0.55)', margin: 0 }}>Voice Slug *</label>
                <input
                  style={{ ...inp, width: 180, fontSize: 12, fontFamily: 'monospace', background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)', color: '#F0F9FF' }}
                  value={values.voice_slug}
                  placeholder="aria-downtown"
                  onChange={(e) => {
                    set('voice_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                    setSlugError('');
                  }}
                />
                {slugError && <span style={{ fontSize: 10, color: '#FCA5A5' }}>{slugError}</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <label style={{ ...lbl, color: 'rgba(147,197,253,0.55)', margin: 0 }}>Active</label>
                <Toggle checked={values.is_active} onChange={(v) => set('is_active', v)} />
              </div>
            </div>
          </div>
        </ModalHeader>

        <div style={{ borderBottom: '1px solid #E2E8F0', display: 'flex', padding: '0 16px', background: '#F8FAFC', overflowX: 'auto' }}>
          {TABS.map((t) => (
            <button key={t.key} type="button" style={tabStyle(t.key)} onClick={() => setActiveTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        <ModalBody style={{ padding: 0, height: '68vh', overflowY: 'auto' }}>
          {loadingConfig ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, color: '#64748B' }}>
              <Spinner /> <span style={{ fontSize: 13 }}>Loading configuration…</span>
            </div>
          ) : (
            <div style={{ padding: '22px 26px' }}>
              {activeTab === 'persona' && <PersonaSection values={values} set={set} />}
              {activeTab === 'ai' && <AISection values={values} set={set} />}
              {activeTab === 'stt' && <STTSection values={values} set={set} />}
              {activeTab === 'tts' && <TTSSection values={values} set={set} />}
              {activeTab === 'integrations' && <IntegrationsSection values={values} set={set} propertyList={propertyList} />}
              {activeTab === 'call_limits' && <CallLimitsSection values={values} set={set} />}
              {activeTab === 'lead_config' && <LeadConfigSection values={values} set={set} />}
            </div>
          )}
        </ModalBody>

        <ModalFooter style={{ background: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
          <Button outline color="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button color="primary" onClick={handleSubmit} disabled={saving} style={{ minWidth: 140 }}>
            {saving && <Spinner size="sm" className="me-2" />}
            {saving ? 'Saving…' : isEditMode ? '✓ Update Config' : '✓ Create Assistant'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export { VoiceAssistantConfigModal };